// Servizi dominio HOTEL (camere, prenotazioni, extra, impostazioni, rimborsi, iCal,
// email, conversazioni, checkout, report) + endpoint pubblici della vetrina.
// Riusa l'istanza axios centrale (lib/api.js). Convenzioni:
//   GET:  (params?, config?)  -> api.get(path, { params, ...config })
//   POST/PUT: (body?, config?) -> api.post(path, body, config)
import { api, unwrap } from './client';

/** @typedef {import('@/types/api').components['schemas']} S */

const g = (path, params, config) => unwrap(api.get(path, { params, ...(config || {}) }));
const p = (path, body, config) => unwrap(api.post(path, body, config));
const u = (path, body, config) => unwrap(api.put(path, body, config));
const d = (path, config) => unwrap(api.delete(path, config));

export const hotelService = {
  // --- Camere ---
  options: () => g('/hotel/options'),
  listRooms: (params) => g('/hotel/rooms', params),
  getRoom: (id) => g(`/hotel/rooms/${id}`),
  /** @param {S['RoomIn']} body */
  createRoom: (body) => p('/hotel/rooms', body),
  /** @param {S['RoomIn']} body */
  updateRoom: (id, body) => u(`/hotel/rooms/${id}`, body),
  deleteRoom: (id) => d(`/hotel/rooms/${id}`),
  duplicateRoom: (id) => p(`/hotel/rooms/${id}/duplicate`),
  setRoomStatus: (id, body) => p(`/hotel/rooms/${id}/status`, body),
  roomPricePreview: (body) => p('/hotel/rooms/price-preview', body),

  // --- Prenotazioni ---
  listBookings: (params) => g('/hotel/bookings', params),
  getBooking: (id) => g(`/hotel/bookings/${id}`),
  /** @param {S['BookingIn']} body */
  createBooking: (body) => p('/hotel/bookings', body),
  updateBooking: (id, body) => u(`/hotel/bookings/${id}`, body),
  bookingAction: (id, body) => p(`/hotel/bookings/${id}/action`, body),
  cancelBooking: (id, body) => p(`/hotel/bookings/${id}/cancel`, body),
  resolveCancellation: (id, body) => p(`/hotel/bookings/${id}/cancellation/resolve`, body),
  bookingMessage: (id, body) => p(`/hotel/bookings/${id}/message`, body),
  markChatSeen: (id) => p(`/hotel/bookings/${id}/chat/seen`),
  approveExtra: (id, itemId) => p(`/hotel/bookings/${id}/extras/${itemId}/approve`),
  rejectExtra: (id, itemId) => p(`/hotel/bookings/${id}/extras/${itemId}/reject`),
  runCheckinReminders: () => p('/hotel/bookings/reminders/run-now'),
  arrivalsToday: (day) => g('/hotel/arrivals-today', day ? { day } : undefined),
  downloadBookingDocument: (id, docId) => api.get(`/hotel/bookings/${id}/documents/${docId}`, { responseType: 'blob' }),
  hotelChatAttachment: (id, file, text = '') => {
    const fd = new FormData();
    fd.append('file', file);
    if (text) fd.append('text', text);
    return p(`/hotel/bookings/${id}/chat/attachment`, fd, { headers: { 'Content-Type': undefined } });
  },
  markDocumentSeen: (bookingId, docId) => p(`/hotel/bookings/${bookingId}/documents/${docId}/seen`),
  checkAvailability: (body) => p('/hotel/bookings/availability', body),
  priceBooking: (body) => p('/hotel/bookings/price', body),

  // --- Verbale di Consegna / Riconsegna stato immobile (host) ---
  handoverBookings: () => g('/hotel/handover/bookings'),
  handoverGet: (bookingId) => g(`/hotel/handover/booking/${bookingId}`),
  handoverCreate: (bookingId, phase = 'consegna') => p(`/hotel/handover/booking/${bookingId}?phase=${encodeURIComponent(phase)}`),
  handoverUpdate: (hid, body) => u(`/hotel/handover/${hid}`, body),
  handoverComplete: (hid) => p(`/hotel/handover/${hid}/complete`),
  handoverAddPhoto: (hid, areaId, file) => {
    const fd = new FormData();
    fd.append('area_id', areaId);
    fd.append('file', file);
    return p(`/hotel/handover/${hid}/photo`, fd, { headers: { 'Content-Type': undefined } });
  },
  handoverRemovePhoto: (hid, areaId, photoId) => d(`/hotel/handover/${hid}/photo/${photoId}?area_id=${encodeURIComponent(areaId)}`),

  // --- Recensioni (CRM) ---
  listReviews: () => g('/hotel/reviews'),
  replyReview: (reviewId, reply) => p(`/hotel/reviews/${reviewId}/reply`, { reply }),

  // --- Calendario / audit / report ---
  calendar: (params) => g('/hotel/calendar', params),
  audit: (params) => g('/hotel/audit', params),
  profitReport: (params) => g('/hotel/reports/profit', params),

  // --- Extra ---
  listExtras: () => g('/hotel/extras'),
  createExtra: (body) => p('/hotel/extras', body),
  updateExtra: (id, body) => u(`/hotel/extras/${id}`, body),
  deleteExtra: (id) => d(`/hotel/extras/${id}`),
  reorderExtras: (body) => p('/hotel/extras/reorder', body),

  // --- Impostazioni ---
  getSettings: () => g('/hotel/settings'),
  updateSettings: (body) => u('/hotel/settings', body),
  updateCancellationPolicy: (body) => u('/hotel/settings/cancellation-policy', body),
  updateLanding: (body) => u('/hotel/settings/landing', body),
  updateSettingsStructure: (body) => u('/hotel/settings/structure', body),
  updateSettingsOperativity: (body) => u('/hotel/settings/operativity', body),
  updateSettingsCalendars: (body) => u('/hotel/settings/calendars', body),
  updateSettingsDocuments: (body) => u('/hotel/settings/documents', body),
  updateSettingsAutomations: (body) => u('/hotel/settings/automations', body),
  updateSelfCheckin: (body) => u('/hotel/settings/self-check-in', body),
  updateMaps: (body) => u('/hotel/settings/maps', body),
  paymentsStatus: () => g('/hotel/settings/payments-status'),

  // --- Checkout ---
  getCheckoutConfig: () => g('/hotel/checkout-config'),
  updateCheckoutConfig: (body) => u('/hotel/checkout-config', body),
  checkoutPreviewData: () => g('/hotel/checkout-preview-data'),

  // --- Rimborsi ---
  listRefunds: (params) => g('/hotel/refunds', params),
  refundsStats: () => g('/hotel/refunds/stats'),
  refundsSuggestions: () => g('/hotel/refunds/suggestions'),
  previewRefund: (body) => p('/hotel/refunds/preview', body),
  executeRefund: (body) => p('/hotel/refunds/execute', body),

  // --- iCal ---
  listIcalFeeds: () => g('/hotel/ical-feeds'),
  createIcalFeed: (body) => p('/hotel/ical-feeds', body),
  deleteIcalFeed: (id) => d(`/hotel/ical-feeds/${id}`),
  syncIcalFeed: (id) => p(`/hotel/ical-feeds/${id}/sync`),
  syncAllIcalFeeds: () => p('/hotel/ical-feeds/sync-all'),
  icalExport: (params, config) => g('/hotel/ical-export', params, config),

  // --- Email templates ---
  listEmailTemplates: () => g('/hotel/email-templates'),
  emailPlaceholders: () => g('/hotel/email-templates/placeholders'),
  getEmailTemplate: (id) => g(`/hotel/email-templates/${id}`),
  updateEmailTemplate: (id, body) => u(`/hotel/email-templates/${id}`, body),
  previewEmailTemplate: (id, body) => p(`/hotel/email-templates/${id}/preview`, body),
  resetEmailTemplate: (id) => p(`/hotel/email-templates/${id}/reset`),
  sendTestEmailTemplate: (id, body) => p(`/hotel/email-templates/${id}/send-test`, body),

  // --- Conversazioni / messaggi ---
  listConversations: (params) => g('/hotel/conversations', params),
  getConversation: (id) => g(`/hotel/conversations/${id}`),
  sendConversationMessage: (id, body) => p(`/hotel/conversations/${id}/messages`, body),
  readConversation: (id) => p(`/hotel/conversations/${id}/read`),
  readAllConversations: () => p('/hotel/conversations/read-all'),
  simulateGuest: (id, body) => p(`/hotel/conversations/${id}/simulate-guest`, body),

  // --- Vetrina pubblica ---
  publicStructures: (params) => g('/hotel/public/structures', params),
  publicStructure: (id) => g(`/hotel/public/structures/${id}`),
  publicRoom: (id) => g(`/hotel/public/rooms/${id}`),
  publicRoomOccupied: (id, ownerId, months) => g(`/hotel/public/rooms/${id}/occupied`, { owner_id: ownerId, months }),
  publicAvailability: (body) => p('/hotel/public/availability', body),
  publicBook: (body) => p('/hotel/public/book', body),
};
